// SPDX-License-Identifier: AGPL-3.0-or-later

import { Navigate, Outlet, useLocation } from 'react-router';
import { useLogout, useMe } from '../api/auth.js';
import { useResponses } from '../api/responses.js';
import { isActivelyFlagged } from '../api/types.js';
import { ClinicianCommandPalette } from './ClinicianCommandPalette.js';
import { Sidebar } from './Sidebar.js';
import { OrgThemeStyles } from '../theme/index.js';

export function ProtectedShell() {
  const { data: me, isLoading } = useMe();
  const logout = useLogout();
  const location = useLocation();
  const responses = useResponses();

  if (isLoading) {
    return (
      <div className="app-main">
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (!me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Active = flagged AND not in a terminal status (verified /
  // completed / entered-in-error). Once a clinician signs off, the
  // response leaves this count even though the `flagged` boolean
  // stays set as an audit fact — see isActivelyFlagged in
  // apps/web/src/api/types.ts.
  const flaggedCount = responses.data?.filter(isActivelyFlagged).length ?? 0;
  // Badge key must match the Sidebar's Responses link `to` exactly so a
  // click on the badge lands on the list that surfaces those rows.
  const badges = {
    '/responses': flaggedCount,
  };

  return (
    <>
      <OrgThemeStyles settings={me.organization?.settings} />
      <div data-ui-app-shell="">
        <a className="skip-link" href="#main-content">
          Skip to main content
        </a>
        <Sidebar
          me={me}
          onSignOut={() => logout.mutate(undefined)}
          isSigningOut={logout.isPending}
          badges={badges}
        />
        <main className="app-main" id="main-content" tabIndex={-1} data-ui-app-main="">
          <Outlet />
        </main>
        <ClinicianCommandPalette />
      </div>
    </>
  );
}
